import { useId } from 'react';

import type { CatalogJsonSchema, CatalogSchemaProperty } from '@platform/contracts';

import { Checkbox } from './choice';
import { Field } from './field';
import { Input } from './input';
import { Select } from './select';

/**
 * The form the catalog's JSON Schema draws (MASTER_PLAN M3.9, M16.2, INTEGRATIONS.md §10.1).
 *
 * A super admin authors `settingsSchema` and `credentialSchema`; the clinic admin's
 * browser renders them, and since M16.2 (ADR-033) so does the super console's install
 * detail. It lives here, in `@platform/ui`, so both apps draw the same form from the same
 * code and nothing in it knows which app it is in. That makes this a *renderer for one documented dialect*, not a
 * general JSON Schema engine: `CatalogJsonSchemaSchema` in `@platform/contracts` admits
 * exactly `type`, `title`, `description`, `secret`, `format`, `pattern`, `enum`,
 * `default`, `minimum`/`maximum`, `minLength`/`maxLength` and one level of `items`, and
 * rejects everything else on the way in. Every one of those is handled here.
 *
 * It is deliberately not `FormRenderer` (also in this package). That one renders the intake
 * form's own `FormDefinition` — sections, conditional visibility, PHI-bearing answer sets.
 * These are five flat fields of clinic configuration, and pushing them through the intake
 * schema would mean translating one dialect into another so a bigger component could
 * render a smaller thing (rules §17.9).
 *
 * ## Secrets
 *
 * A field marked `secret: true` renders as a password input that is **never prefilled**:
 * the API has no way to return one (SECURITY.md §7.4), so an empty box is the truth.
 * What is shown beside it is the `credHint` mask, which is how an admin recognises which
 * key is installed without being able to recover it. The typed value lives in the
 * caller's state and nowhere else, and the caller clears it on submit.
 */

/** Everything the two forms can hold. `string[]` is the one-level `items` case. */
export type SchemaValue = string | number | boolean | string[] | undefined;
export type SchemaValues = Record<string, SchemaValue>;

/**
 * A property this renderer has no control for, and deliberately.
 *
 * `CatalogJsonSchemaSchema` admits `type: 'object'` but gives it no `properties`, so there
 * is nothing to draw: the dialect can *say* "an object goes here" and cannot say what is in
 * it. One property uses it — HighLevel's `crmMapping` (M3.12) — and it has its own screen,
 * the Marketing sync tab, which validates it against `CrmMappingSchema` at
 * `PUT .../highlevel/mapping`.
 *
 * Skipping it is not cosmetic. Rendering it as a text box would round-trip a stored mapping
 * through `String(value)` and PATCH back `"[object Object]"` — a consent gate flattened into
 * a string by a form that was only trying to be generic. Left out of the values entirely,
 * `PATCH /settings` merges and the stored mapping is untouched.
 */
function isNested(property: CatalogSchemaProperty): boolean {
  return property.type === 'object';
}

/**
 * Which fields the admin's current choice shows (a `oneOf` whose branches carry `when`).
 *
 * HighLevel takes a Private Integration token *or* an OAuth app, and `kind` says which.
 * Showing all five boxes for either kind is how an admin pasted a token into the OAuth
 * "Access token" box and got a union error naming no field (live, 2026-09-29). So: the
 * `when` fields always show; a field that belongs to some branch shows only with the
 * chosen branch, and is required when that branch requires it; a field no branch claims
 * shows as before. Until a kind is chosen, only the picker and the shared fields show.
 */
interface SchemaLayout {
  visible: (name: string) => boolean;
  /**
   * Draw order: the picker, then the chosen branch's fields as the branch lists them
   * (required, then optional), then everything else. Postgres `jsonb` re-sorts object
   * keys, so `properties` order does not survive the catalog table.
   */
  rank: (name: string) => number;
  required: ReadonlySet<string>;
  /** The branch title to show for a `when` value, so the picker reads as the choice. */
  optionLabel: (name: string, option: string) => string | undefined;
}

function layoutOf(schema: CatalogJsonSchema, values: SchemaValues): SchemaLayout {
  const required = new Set(schema.required ?? []);
  const branches = (schema.oneOf ?? []).filter((variant) => variant.when !== undefined);
  if (branches.length === 0) {
    return { visible: () => true, rank: () => 0, required, optionLabel: () => undefined };
  }

  const pickers = new Set(branches.flatMap((variant) => Object.keys(variant.when ?? {})));
  const claimed = new Set(
    branches.flatMap((variant) => [...variant.required, ...(variant.optional ?? [])]),
  );
  const chosen = branches.find((variant) =>
    Object.entries(variant.when ?? {}).every(([name, value]) => values[name] === value),
  );
  const shown = new Set(
    chosen === undefined ? [] : [...chosen.required, ...(chosen.optional ?? [])],
  );
  // The picker is required too: until it is set, no branch applies and nothing validates.
  for (const name of [...pickers, ...(chosen?.required ?? [])]) required.add(name);

  const order = [...pickers, ...(chosen?.required ?? []), ...(chosen?.optional ?? [])];
  return {
    visible: (name) => pickers.has(name) || !claimed.has(name) || shown.has(name),
    rank: (name) => (order.includes(name) ? order.indexOf(name) : order.length),
    required,
    optionLabel: (name, option) =>
      branches.find((variant) => variant.when?.[name] === option)?.title,
  };
}

/**
 * The stored choice (`kind`) read back from `credHint`, so updating an install opens on
 * the fields it actually uses. Only a `when` field whose hint is one of its `enum`
 * values is taken; a picker is never a secret, and nothing else is copied.
 */
export function schemaStoredChoice(
  schema: CatalogJsonSchema,
  hints: Record<string, unknown>,
): SchemaValues {
  const pickers = new Set(
    (schema.oneOf ?? []).flatMap((variant) => Object.keys(variant.when ?? {})),
  );
  const values: SchemaValues = {};
  for (const name of pickers) {
    const property = schema.properties[name];
    const hint = hints[name];
    if (property === undefined || property.secret === true || typeof hint !== 'string') continue;
    if (property.enum?.map(String).includes(hint) === true) values[name] = hint;
  }
  return values;
}

/** Only the values of fields the current choice shows, so a stale hidden box is never sent. */
export function schemaVisibleValues(schema: CatalogJsonSchema, values: SchemaValues): SchemaValues {
  const { visible } = layoutOf(schema, values);
  return Object.fromEntries(Object.entries(values).filter(([name]) => visible(name)));
}

export interface SchemaFormProps {
  schema: CatalogJsonSchema;
  values: SchemaValues;
  onChange: (name: string, value: SchemaValue) => void;
  /** Field-level messages, keyed by property name. From `VALIDATION_FAILED` or `validateSchemaValues()`. */
  errors?: Record<string, string>;
  /** `credHint`: what is stored for each field, masked. Shown as help under secrets. */
  hints?: Record<string, string>;
  disabled?: boolean;
  /** Prefix for generated control ids, so two forms on one page never collide. */
  idPrefix?: string;
}

export function SchemaForm({
  schema,
  values,
  onChange,
  errors = {},
  hints = {},
  disabled = false,
  idPrefix,
}: SchemaFormProps) {
  const generated = useId();
  const prefix = idPrefix ?? generated;
  const layout = layoutOf(schema, values);
  const entries = Object.entries(schema.properties).filter(([, property]) => !isNested(property));

  if (entries.length === 0) {
    return <p className="text-base text-neutral-500">This integration has nothing to configure.</p>;
  }

  return (
    <div className="flex flex-col gap-5">
      {entries
        .filter(([name]) => layout.visible(name))
        // A stable sort, so fields outside the branch keep their schema order.
        .sort(([a], [b]) => layout.rank(a) - layout.rank(b))
        .map(([name, property]) => (
          <SchemaField
            key={name}
            id={`${prefix}-${name}`}
            name={name}
            property={property}
            required={layout.required.has(name)}
            optionLabel={(option) => layout.optionLabel(name, option)}
            value={values[name]}
            hint={hints[name]}
            error={errors[name]}
            disabled={disabled}
            onChange={onChange}
          />
        ))}
    </div>
  );
}

interface SchemaFieldProps {
  id: string;
  name: string;
  property: CatalogSchemaProperty;
  required: boolean;
  value: SchemaValue;
  hint: string | undefined;
  error: string | undefined;
  disabled: boolean;
  onChange: (name: string, value: SchemaValue) => void;
  /** A readable label for an `enum` option, when the schema has one. */
  optionLabel?: (option: string) => string | undefined;
}

function SchemaField({
  id,
  name,
  property,
  required,
  value,
  hint,
  error,
  disabled,
  onChange,
  optionLabel,
}: SchemaFieldProps) {
  const label = property.title ?? humanizeSchemaKey(name);
  const isSecret = property.secret === true;

  const help =
    isSecret && hint !== undefined ? (
      <>
        {property.description === undefined ? null : <>{property.description} </>}
        <span>
          Stored now: <span className="font-medium text-neutral-500">{hint}</span>. Leave this empty
          to keep it.
        </span>
      </>
    ) : (
      (property.description ?? undefined)
    );

  /*
   * A boolean is a checkbox, which carries its own label — wrapping it in a <Field> would
   * produce two labels for one control and a screen reader would read both.
   */
  if (property.type === 'boolean') {
    return (
      <div className="flex flex-col">
        <Checkbox
          id={id}
          label={label}
          {...(property.description === undefined ? {} : { description: property.description })}
          checked={value === true}
          disabled={disabled}
          onChange={(event) => {
            onChange(name, event.currentTarget.checked);
          }}
        />
        {error === undefined ? null : (
          <p role="alert" className="mt-[5px] text-sm text-danger-ink">
            <span aria-hidden="true">&#9888; </span>
            {error}
          </p>
        )}
      </div>
    );
  }

  return (
    <Field
      id={id}
      label={label}
      required={required}
      {...(help === undefined ? {} : { help })}
      {...(error === undefined ? {} : { error })}
    >
      {property.enum !== undefined && property.enum.length > 0 ? (
        <Select
          value={value === undefined ? '' : String(value)}
          disabled={disabled}
          onChange={(event) => {
            onChange(name, fromText(property, event.currentTarget.value));
          }}
        >
          <option value="">Choose one</option>
          {property.enum.map((option) => (
            <option key={String(option)} value={String(option)}>
              {optionLabel?.(String(option)) ?? String(option)}
            </option>
          ))}
        </Select>
      ) : (
        <Input
          type={inputType(property)}
          /* A secret is never prefilled: the API cannot return one, so an empty box is honest. */
          value={
            value === undefined
              ? ''
              : Array.isArray(value)
                ? value.join(', ')
                : property.format === 'date-time'
                  ? toLocalInput(String(value))
                  : String(value)
          }
          disabled={disabled}
          autoComplete={isSecret ? 'new-password' : 'off'}
          {...(isSecret ? { spellCheck: false } : {})}
          {...(property.minLength === undefined ? {} : { minLength: property.minLength })}
          {...(property.maxLength === undefined ? {} : { maxLength: property.maxLength })}
          {...(property.minimum === undefined ? {} : { min: property.minimum })}
          {...(property.maximum === undefined ? {} : { max: property.maximum })}
          {...(property.type === 'integer' ? { step: 1 } : {})}
          placeholder={property.type === 'array' ? 'Separate values with commas' : undefined}
          onChange={(event) => {
            onChange(name, fromText(property, event.currentTarget.value));
          }}
        />
      )}
    </Field>
  );
}

/* ------------------------------------------------------------------ values */

/**
 * The form's starting values: what is stored, then the schema's `default`, then empty.
 *
 * `secret` properties are skipped whatever `stored` says, so a hint can never be typed
 * back into the box it describes and re-submitted as if it were the key.
 */
export function schemaInitialValues(
  schema: CatalogJsonSchema,
  stored: Record<string, unknown> = {},
): SchemaValues {
  const values: SchemaValues = {};
  for (const [name, property] of Object.entries(schema.properties)) {
    if (property.secret === true || isNested(property)) continue;
    const current = stored[name] ?? property.default;
    if (current === undefined || current === null) continue;
    values[name] = Array.isArray(current)
      ? current.map((item) => String(item))
      : typeof current === 'number' || typeof current === 'boolean'
        ? current
        : String(current);
  }
  return values;
}

/** Drops empty strings and undefined, so a PATCH never clears a field the admin ignored. */
export function schemaSubmittedValues(values: SchemaValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [name, value] of Object.entries(values)) {
    if (value === undefined || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    out[name] = value;
  }
  return out;
}

/* -------------------------------------------------------------- validation */

/**
 * The browser's copy of the schema's rules — a courtesy, never the enforcement point
 * (rules §17.2, §17.5). The API validates every value against the catalog's schema and
 * its answer, not this, decides whether a save happened.
 *
 * `omitEmptySecrets` is what makes "update one of three keys" work: on an install that
 * already has credentials, a blank secret means "keep the stored one" rather than
 * "required field missing".
 */
export function validateSchemaValues(
  schema: CatalogJsonSchema,
  values: SchemaValues,
  options: { omitEmptySecrets?: boolean } = {},
): Record<string, string> {
  const errors: Record<string, string> = {};
  const { visible, required } = layoutOf(schema, values);

  for (const [name, property] of Object.entries(schema.properties)) {
    /* No control, so no value and nothing to validate — the API owns this one. */
    if (isNested(property) || !visible(name)) continue;

    const value = values[name];
    const empty =
      value === undefined || value === '' || (Array.isArray(value) && value.length === 0);

    if (empty) {
      const skip = property.secret === true && options.omitEmptySecrets === true;
      if (required.has(name) && !skip) errors[name] = 'This one is needed.';
      continue;
    }

    if (property.type === 'number' || property.type === 'integer') {
      if (typeof value !== 'number' || Number.isNaN(value)) {
        errors[name] = 'Enter a number.';
        continue;
      }
      if (property.type === 'integer' && !Number.isInteger(value)) {
        errors[name] = 'Enter a whole number.';
        continue;
      }
      if (property.minimum !== undefined && value < property.minimum) {
        errors[name] = `Enter ${String(property.minimum)} or more.`;
        continue;
      }
      if (property.maximum !== undefined && value > property.maximum) {
        errors[name] = `Enter ${String(property.maximum)} or less.`;
        continue;
      }
    }

    if (typeof value === 'string') {
      if (property.minLength !== undefined && value.length < property.minLength) {
        errors[name] = `Use at least ${String(property.minLength)} characters.`;
        continue;
      }
      if (property.maxLength !== undefined && value.length > property.maxLength) {
        errors[name] = `Use at most ${String(property.maxLength)} characters.`;
        continue;
      }
      if (property.pattern !== undefined && !matches(property.pattern, value)) {
        errors[name] = 'That is not in the expected format.';
        continue;
      }
      if (
        property.format === 'date-time' &&
        (!/^\d{4}-\d{2}-\d{2}T/.test(value) || Number.isNaN(new Date(value).getTime()))
      ) {
        errors[name] = 'Pick a date and time.';
        continue;
      }
      if (property.format === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        errors[name] = 'Enter an email address.';
        continue;
      }
      if (property.format === 'uri' && !isUrl(value)) {
        errors[name] = 'Enter a web address, starting with https://.';
        continue;
      }
    }

    if (property.enum !== undefined && !property.enum.some((option) => option === value)) {
      errors[name] = 'Choose one of the listed values.';
    }
  }

  return errors;
}

/**
 * `VALIDATION_FAILED` issues mapped onto the fields that caused them (API.md §1.6).
 *
 * Paths arrive as `["credentials", "apiKey"]` or `["settings", "domain"]`; the last
 * segment that names a declared property wins, so a change of envelope on the API side
 * does not silently drop every message on the floor. Anything that matches nothing is
 * returned under `''`, which the caller shows as a form-level message rather than losing.
 */
export function schemaIssuesToFieldErrors(
  schema: CatalogJsonSchema,
  issues: { path: (string | number)[]; message: string }[],
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const issue of issues) {
    const named = [...issue.path]
      .reverse()
      .find((segment) => typeof segment === 'string' && segment in schema.properties);
    const key = typeof named === 'string' ? named : '';
    if (errors[key] === undefined) errors[key] = issue.message;
  }
  return errors;
}

/* ----------------------------------------------------------------- helpers */

function inputType(property: CatalogSchemaProperty): string {
  if (property.secret === true) return 'password';
  if (property.type === 'number' || property.type === 'integer') return 'number';
  if (property.format === 'email') return 'email';
  if (property.format === 'uri') return 'url';
  if (property.format === 'date') return 'date';
  if (property.format === 'date-time') return 'datetime-local';
  return 'text';
}

/**
 * `datetime-local` speaks the viewer's wall clock with no zone (`2026-09-01T09:15`); the
 * API speaks ISO instants (`2026-09-01T16:15:00.000Z`). These two translate at the
 * control and nowhere else, so what is stored is always an instant.
 */
function toLocalInput(value: string): string {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return value;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${String(instant.getFullYear())}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}T${pad(instant.getHours())}:${pad(instant.getMinutes())}`;
}

function fromLocalInput(text: string): string {
  const instant = new Date(text);
  return Number.isNaN(instant.getTime()) ? text : instant.toISOString();
}

/** One typed value from what was typed. An unparseable number stays `NaN` so `validateSchemaValues` names it. */
function fromText(property: CatalogSchemaProperty, text: string): SchemaValue {
  if (text === '') return undefined;
  if (property.format === 'date-time') return fromLocalInput(text);
  if (property.type === 'number' || property.type === 'integer') return Number(text);
  if (property.type === 'boolean') return text === 'true';
  if (property.type === 'array') {
    return text
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== '');
  }
  return text;
}

/**
 * A schema's `pattern` is authored by a super admin, not by a visitor, but it is still a
 * string compiled into a regular expression: an invalid one must not take the page down
 * with it, so a pattern that will not compile passes rather than throws. The server
 * applies the same pattern and refuses the value if it really does not match.
 */
function matches(pattern: string, value: string): boolean {
  try {
    return new RegExp(pattern).test(value);
  } catch {
    return true;
  }
}

function isUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

/** `webhookSigningKey` -> `Webhook signing key`, for a property with no `title`. */
export function humanizeSchemaKey(name: string): string {
  const spaced = name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .toLowerCase()
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
